<script setup lang="ts">
import { scopeWpHtml } from '@/utils/scopeWpHtml';
import { executeWpScripts } from '@/utils/executeWpScripts';
import {
  fetchBlogPostsHandler,
  clearBlogPostsCache,
} from '~/handlers/fetch-blog-posts.handler';
import { fetchBlogPostHandler } from '~/handlers/fetch-blog-post.handler';
import { getPromotionsCategoryId } from '~/utils/apostatudo/get-promotions-category-id';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';
import {
  mapCategoriesToWp,
  mapPostToWp,
  type WpStylePost,
} from '~/utils/apostatudo/map-core-to-wp';

const route = useRoute('promotions-promotion');

const { blogPostClickEvent } = useUserJourney();

const isLoading = ref<boolean>(false);
const isLoadingDetail = ref<boolean>(false);
const wpPosts = ref<WpStylePost[]>([]);
const detailItem = ref<WpStylePost | null>(null);

const promoSlug = computed<string>(() => {
  const raw = route.params.promotion;

  return Array.isArray(raw) ? raw[0] : raw || '';
});

const singleItem = computed<WpStylePost | null>(() => {
  if (!promoSlug.value) return null;

  if (detailItem.value && detailItem.value.slug === promoSlug.value) {
    return detailItem.value;
  }

  return wpPosts.value.find((p) => p.slug === promoSlug.value) ?? null;
});

const STATE_ORDER: Record<string, number> = { ACTIVE: 0, ENDED: 1 };

const wpPostsWithStatus = computed(() =>
  [...wpPosts.value]
    .filter((p) => p.state === 'ACTIVE' || p.state === 'ENDED')
    .sort(
      (a, b) => (STATE_ORDER[a.state] ?? 99) - (STATE_ORDER[b.state] ?? 99),
    ),
);

async function getPosts() {
  isLoading.value = true;

  try {
    const categoryId = getPromotionsCategoryId();

    if (!categoryId) {
      wpPosts.value = [];

      return;
    }

    const { categories } = await fetchBlogPostsHandler({
      category: categoryId,
    });

    const { posts } = mapCategoriesToWp(categories);

    wpPosts.value = posts;
  } catch (error) {
    console.error('Erro ao buscar promoções:', error);
  } finally {
    isLoading.value = false;
  }
}

async function loadDetail(slug: string) {
  if (!slug) {
    detailItem.value = null;
    isLoadingDetail.value = false;

    return;
  }

  isLoadingDetail.value = true;

  try {
    const { post } = await fetchBlogPostHandler(slug);

    if (!post) {
      detailItem.value = null;

      return;
    }

    detailItem.value = mapPostToWp(post, [], post.html_content || '');

    if (import.meta.client) blogPostClickEvent(slug);
  } catch (err) {
    console.error('[PromotionsCore] failed to load post detail', err);
    detailItem.value = null;
  } finally {
    isLoadingDetail.value = false;
  }
}

watch(
  promoSlug,
  (slug) => {
    if (import.meta.client) loadDetail(slug);
  },
  { immediate: true },
);

async function refreshList() {
  clearBlogPostsCache();
  await getPosts();
}

const { isMobile } = useLayout();
const { openPreview } = useGameMobilePreview();

const contentRef = ref<HTMLElement | null>(null);
let cleanupWpScripts: (() => void) | null = null;
let cleanupGameLinks: (() => void) | null = null;

const RESERVED_GAMES_SEGMENTS = [
  'category',
  'providers',
  'live',
  'jogos-do-padrinho',
];

function extractGameSlug(href: string): string | null {
  let pathname = '';

  try {
    pathname = new URL(href, window.location.origin).pathname;
  } catch {
    pathname = (href.split('?')[0] ?? '').split('#')[0] ?? '';
  }

  const match = pathname.match(/^\/games\/([^/]+)\/([^/]+)\/?$/);

  if (!match) return null;

  const [, provider, game] = match;

  if (!provider || !game) return null;
  if (RESERVED_GAMES_SEGMENTS.includes(provider)) return null;

  return `${provider}/${game}`;
}

function interceptGameLinks(container: HTMLElement): () => void {
  async function handleClick(e: MouseEvent) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    const anchor = (e.target as HTMLElement).closest('a');

    if (!anchor) return;
    if (!container.contains(anchor)) return;

    const href = anchor.getAttribute('href') ?? '';
    const slug = extractGameSlug(href);

    if (!slug) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (!isMobile.value) {
      const [provider, game] = slug.split('/');

      if (provider && game) {
        navigateTo({
          name: 'games-provider-game',
          params: { provider, game },
        });
      }

      return;
    }

    try {
      const { getApostatudoApiUrl } = await import(
        '~/utils/apostatudo/get-apostatudo-api-url'
      );

      const res = await fetch(`${getApostatudoApiUrl()}/games?slug=${slug}`, {
        headers: { Accept: 'application/json' },
      });

      const data = await res.json();

      openPreview({
        id: data.id,
        slug: data.slug ?? slug,
        name: data.name,
        image: data.image,
        provider: data.provider,
        provider_id: data.provider_id,
        reference: data.reference,
        casino_game_type_id: data.casino_game_type_id,
        demo: data.demo,
        rtp: data.rtp,
        tags: data.tags ?? [],
        ribbon: data.ribbon ?? null,
      } as any);
    } catch {}
  }

  document.addEventListener('click', handleClick, true);

  return () => document.removeEventListener('click', handleClick, true);
}

watch(
  () => singleItem.value?.content?.rendered,
  async (content) => {
    cleanupWpScripts?.();
    cleanupGameLinks?.();
    cleanupWpScripts = null;
    cleanupGameLinks = null;
    if (!content) return;
    await nextTick();
    if (contentRef.value) {
      cleanupWpScripts = executeWpScripts(contentRef.value);
      cleanupGameLinks = interceptGameLinks(contentRef.value);
    }
  },
  { flush: 'post', immediate: true },
);

onUnmounted(() => {
  cleanupWpScripts?.();
  cleanupGameLinks?.();
});

useSeoMeta({
  title: () => singleItem.value?.title?.rendered || undefined,
  description: () =>
    singleItem.value?.yoast_head_json?.og_description || undefined,
  ogTitle: () => singleItem.value?.title?.rendered || undefined,
  ogDescription: () =>
    singleItem.value?.yoast_head_json?.og_description || undefined,
  ogImage: () =>
    singleItem.value?.yoast_head_json?.og_image?.[0]?.url || undefined,
  twitterCard: 'summary_large_image',
});

const router = useRouter();

function isPostPath(path: unknown): boolean {
  if (typeof path !== 'string') return false;

  const clean = path.split('?')[0]?.split('#')[0] ?? '';

  return /^\/(blog|promotions)\/[^/]+\/?$/.test(clean);
}

function onBackClick() {
  if (promoSlug.value) {
    navigateTo({ path: '/promotions' });

    return;
  }

  const previousPath = (router.options.history.state?.back ?? '') as string;

  if (isPostPath(previousPath)) {
    navigateTo('/');

    return;
  }

  if (typeof window !== 'undefined' && window.history.length > 1) {
    router.back();

    return;
  }

  navigateTo('/');
}

onMounted(() => {
  getPosts();
});
</script>

<template>
  <div class="promotions-wrapper">
    <section class="promotions-container">
      <BaseScreenHeader
        heading-tag="h1"
        :is-refreshing="isLoading"
        :on-back="onBackClick"
        :on-refresh="!promoSlug ? refreshList : undefined"
        :title="
          promoSlug ? $t('casino.game.go_back') : $t('sidebar.promotions')
        "
      />

      <section
        v-if="promoSlug"
        :class="[
          'promotions-view promotions-content-sandbox',
          { 'promotions-view-ended': singleItem?.state === 'ENDED' },
        ]"
      >
        <div
          v-if="isLoadingDetail"
          class="loading"
        >
          <div
            class="load skeleton-loading"
            style="height: 400px"
          ></div>
          <div
            v-for="(_n, index) in 30"
            :key="`skeleton-${index}`"
            class="load skeleton-loading"
            style="height: 20px; margin: 15px 0"
          ></div>
        </div>

        <div
          v-if="singleItem?.content?.rendered"
          ref="contentRef"
          class="cactus-promotions content wp-content"
        >
          <div
            class="promotions-content-sandbox"
            v-html="
              scopeWpHtml(
                singleItem.content.rendered,
                '.promotions-content-sandbox',
              )
            "
          ></div>
        </div>

        <div
          v-if="!singleItem && !isLoadingDetail"
          class="alert alert-warning"
        >
          <span>{{ $t('no_promotions_found') }}</span>
        </div>
      </section>

      <section
        v-else-if="wpPostsWithStatus.length"
        class="promotions-section"
      >
        <nuxt-link
          v-for="promo in wpPostsWithStatus"
          :key="promo.slug"
          :class="['promo', { 'promo-ended': promo.state === 'ENDED' }]"
          :draggable="false"
          :to="`/promotions/${promo.slug}`"
        >
          <div class="promo-content">
            <div
              class="promo-image-wrapper"
              :style="{
                border: `1px solid ${
                  promo.state === 'ENDED' ? '#6b7280' : '#ff450c'
                }`,
              }"
            >
              <div
                v-if="promo.ribbon?.text"
                class="promo-status-badge"
                :style="{
                  background: promo.ribbon.bg_color || undefined,
                  color: promo.ribbon.font_color || undefined,
                }"
              >
                {{ promo.ribbon.text }}
              </div>

              <div
                v-if="promo.yoast_head_json.og_image.length"
                class="promo-image"
              >
                <img
                  :alt="promo.title.rendered"
                  :draggable="false"
                  :src="promo.yoast_head_json.og_image[0]?.url"
                />
              </div>
            </div>

            <div class="promo-info">
              <div class="promo-text">
                <div
                  v-if="promo.title.rendered"
                  class="title"
                >
                  <div>{{ promo.title.rendered }}</div>
                </div>

                <div class="promo-excerpt">
                  {{ promo.excerpt.rendered }}
                </div>
              </div>
            </div>
          </div>

          <div class="promo-footer">
            <div class="promo-date">
              <NuxtIcon
                class="date-icon"
                name="blog/calendar"
              />
              <div class="date-text">
                <span class="md:hidden">
                  {{
                    promo.date
                      ? new Date(promo.date).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })
                      : ''
                  }}
                </span>
                <span class="hidden md:inline">
                  {{
                    promo.date
                      ? new Date(promo.date).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })
                      : ''
                  }}
                </span>
              </div>
            </div>
            <div class="promo-read-more">
              <div class="read-more-text">LER MAIS</div>
              <NuxtIcon
                class="read-more-icon"
                name="mission/button/participar"
              />
            </div>
          </div>
        </nuxt-link>
      </section>
      <section
        v-else-if="isLoading"
        class="promotions-section loading"
      >
        <div
          v-for="(_n, index) in 10"
          :key="`skeleton-${index}`"
          class="promo skeleton-loading"
          style="height: 280px"
        ></div>
      </section>
      <section
        v-else
        class="promotions-section"
      >
        <div class="alert alert-warning">
          <span>{{ $t('no_promotions_found') }}</span>
        </div>
      </section>
    </section>
  </div>
</template>

<style scoped lang="scss">
.promotions-view-ended {
  filter: grayscale(1);
  opacity: 0.85;
}

.promotions-view {
  @apply flex justify-between items-stretch flex-wrap gap-[15px] md:gap-[25px] text-left;
  user-select: none;
  -webkit-user-select: none;

  a,
  img {
    -webkit-user-drag: none;
  }
  .loading {
    @apply w-full;
    .load {
      @apply flex-1 min-w-[90%] max-w-[100%] rounded overflow-hidden;
    }
  }
  .thumb {
    @apply w-full overflow-hidden relative object-cover object-center bg-bg-secondary flex items-center justify-center rounded;
    img {
      @apply min-w-full min-h-full max-h-full w-auto h-auto object-cover object-center;
    }
  }
  .title {
    @apply text-primary text-base md:text-2xl font-semibold -mt-2;
  }
  .content {
    @apply w-full overflow-hidden;
    img {
      @apply max-w-full;
    }
  }
}

.promotions-section {
  @apply flex justify-start items-stretch flex-wrap gap-[15px] md:gap-[25px];
  user-select: none;
  -webkit-user-select: none;

  a,
  img {
    -webkit-user-drag: none;
  }

  .promo {
    @apply p-2.5 md:p-5 rounded-[10px] backdrop-blur-[10px] overflow-hidden cursor-pointer no-underline;
    @apply w-full md:w-[calc(50%-12.5px)] lg:w-[calc(33.333%-16.667px)];
    background-color: rgb(3, 7, 18);
    outline: 1px solid rgb(38, 38, 38);
    outline-offset: -1px;
    transition: opacity 0.2s;
    display: flex;
    flex-direction: column;

    &:hover {
      @apply opacity-90;
    }

    &.promo-ended {
      .promo-image-wrapper img {
        filter: grayscale(1);
      }

      .promo-text .title,
      .promo-text .promo-excerpt {
        @apply text-white/40;
      }
    }

    .promo-content {
      @apply flex flex-col justify-start items-start gap-5;
      flex: 1;

      .promo-image-wrapper {
        @apply w-full rounded-[10px] flex flex-col justify-between items-center;
        overflow: visible;
        position: relative;

        .promo-status-badge {
          @apply px-3 py-1 text-[10px] font-extrabold uppercase rounded-md;
          font-family: 'Plus Jakarta Sans', sans-serif;
          letter-spacing: 0.5px;
          width: fit-content;
          position: absolute;
          top: 0;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
        }

        .promo-image {
          @apply w-full relative overflow-hidden rounded-[10px] bg-black;
          aspect-ratio: 1240 / 541;

          img {
            @apply w-full h-full object-contain object-center rounded-[10px] block;
          }
        }
      }

      .promo-info {
        @apply w-full flex flex-col justify-start items-start gap-0;

        .promo-text {
          @apply w-full px-0 md:px-2.5 pb-0 flex flex-col justify-center items-start gap-2.5;

          .title {
            @apply w-full text-white font-bold;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 24px !important;
            line-height: 1.2 !important;

            :deep(:where(*)) {
              font-size: 24px !important;
              line-height: 1.2 !important;
              font-weight: 700 !important;
              margin: 0 !important;
            }

            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .promo-excerpt {
            @apply w-full text-white/50 font-medium;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 14px;
            line-height: 1.3;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .promo-footer {
      @apply w-full pl-2.5 md:pl-5 pr-2.5 py-1.5 md:py-2.5 bg-white/5 rounded-[10px] flex justify-between items-center mt-5;

      .promo-date {
        @apply px-[5px] md:px-[5px] flex justify-start items-center gap-2;
        width: auto;
        flex: 1;
        margin-top: 0;
        padding: 5px;
        background: transparent;
        border-radius: 0;

        .date-icon {
          @apply text-white/50;
          font-size: 16px;
        }

        .date-text {
          @apply text-white/50 text-xs font-bold leading-4;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
      }

      .promo-read-more {
        @apply h-8 md:h-10 px-2.5 md:px-5 rounded-md flex justify-center items-center gap-2;
        width: auto;
        margin-top: 0;
        background: radial-gradient(
          ellipse 138% 168.3% at 36.76% -16.67%,
          #ff704a 0%,
          #ff3400 100%
        );
        outline: 0.5px solid rgba(255, 195, 195, 0.3);
        outline-offset: -0.5px;

        .read-more-text {
          @apply text-center text-white text-sm font-extrabold leading-3;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .read-more-icon {
          @apply text-white;
          font-size: 16px;
        }
      }
    }
  }
}

.promotions-wrapper {
  @apply w-full -mt-4 lg:-mt-5;
}

.promotions-container {
  @apply mt-0 md:mt-0 mb-6 md:mb-10;
}
</style>

<style lang="scss">
.cactus-promotions.content {
  @apply text-xs md:text-sm text-texts leading-7;
  a {
    &:hover {
      @apply text-primary underline;
    }
  }

  p {
    @apply leading-7 my-4 text-base;
  }

  img {
    @apply max-w-full h-auto inline-block;
  }

  video {
    @apply max-w-full h-auto inline-block;
  }

  h1 {
    @apply text-[2.5rem];
  }

  h2 {
    @apply text-[2rem];
  }

  div:has(> figure > video) {
    @apply grid grid-cols-[50%,_1fr] grid-rows-[auto];
  }

  figcaption {
    @apply opacity-15;
  }

  ul {
    list-style: inside;

    li {
      @apply my-2;
    }
  }

  ol {
    list-style: decimal inside;

    li {
      @apply my-2;
    }
  }

  blockquote {
    @apply my-4 p-4 bg-bg-secondary rounded border-l-4 border-primary text-texts;
    p {
      @apply m-0;
    }
  }

  .section-title {
    @apply text-primary bg-auth-bg-primary rounded-3xl text-lg font-bold p-2 text-center uppercase mt-12;
  }

  .cta {
    @apply text-bg-primary bg-primary rounded-3xl text-lg font-bold p-4 text-center uppercase mt-12;
  }

  .wp-block-table {
    @apply mb-2.5 mt-1;
    table.has-background {
      border-spacing: 1px;
      border-collapse: separate;
      border: 1px solid white;
      background-color: white !important;

      th {
        @apply text-texts/90 bg-sidebar-bg p-2.5 w-dvw text-center;
      }
      tr td:first-child {
        @apply text-texts/100 bg-sidebar-bg p-2.5;
      }
      tr td:nth-child(2) {
        @apply text-texts/100 bg-sidebar-bg p-2.5;
        width: 80%;
      }
    }
  }
  .wp-block-group-is-layout-flex {
    @apply text-secondary flex flex-col items-center justify-center;
    max-height: 200px !important;
    min-height: 180px !important;
    .has-medium-font-size {
      @apply text-lg m-0 p-0;
    }
  }
  div.cta-container {
    margin-bottom: 10px;
  }
}
.wp-block-social-links {
  @apply flex items-center justify-center flex-nowrap list-none text-lg gap-4 my-4 mx-0 p-0;
  a[href='^http'] {
    @apply text-primary hover:opacity-90 no-underline;
  }
  svg {
    @apply h-[1.25em] w-[1.25em] text-current fill-current;
  }
}
.wp-block-button__link {
  @apply text-bg-primary bg-primary rounded-full shadow-none no-underline py-2 px-5 text-base inline-block text-center break-words cursor-pointer hover:text-bg-primary hover:opacity-80 hover:no-underline;
}
.screen-reader-text {
  @apply overflow-hidden w-[1px] h-[1px];
  clip: rect(1px, 1px, 1px, 1px);
  position: absolute !important;
  word-wrap: normal !important;
}
</style>
